import { useEffect, useMemo, useState } from 'react'
import { FileText, Loader2, Search } from 'lucide-react'
import {
  useAdminRequestsOverviewQuotes,
  type RequestResponseFilter,
} from 'src/api/db/apiQuote'
import { PageContainer } from 'src/shared/components/PageContainer'
import { EmptyState } from 'src/shared/components/EmptyState'
import { Column, Row } from 'src/shared/components/Semantic/all'
import { TabNavigation } from 'src/shared/components/TabNavigation'
import { Button } from 'src/shared/components/ui/button'
import { Input } from 'src/shared/components/ui/input'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from 'src/shared/components/ui/table'
import { useDebounce } from 'src/shared/hooks/useDebounce'
import { useLocalization } from 'src/shared/hooks/useLocalization'
import { RequestOverviewRow } from './components/RequestOverviewRow'

const PAGE_SIZE = 40

const overviewTableClass = 'table-fixed w-full'

const overviewHeadClass =
  'h-10 bg-muted/40 text-xs font-medium text-muted-foreground'

const OverviewColGroup = () => (
  <colgroup>
    <col style={{ width: '36%' }} />
    <col style={{ width: '24%' }} />
    <col style={{ width: '9.5rem' }} />
    <col style={{ width: '5.5rem' }} />
    <col style={{ width: '7.5rem' }} />
  </colgroup>
)

const RESPONSE_FILTERS: Array<{
  id: RequestResponseFilter
  labelEn: string
  labelSv: string
}> = [
  { id: 'all', labelEn: 'All', labelSv: 'Alla' },
  {
    id: 'needs_response',
    labelEn: 'Needs response',
    labelSv: 'Behöver svar',
  },
  {
    id: 'awaiting_response',
    labelEn: 'Awaiting response',
    labelSv: 'Väntar på svar',
  },
]

export const AdminRequestsOverviewPage = () => {
  const { t } = useLocalization()
  const [page, setPage] = useState(0)
  const [search, setSearch] = useState('')
  const [responseFilter, setResponseFilter] =
    useState<RequestResponseFilter>('all')
  const debouncedSearch = useDebounce(search, 300)

  useEffect(() => {
    setPage(0)
  }, [debouncedSearch, responseFilter])

  const { data, isPending, isFetching } = useAdminRequestsOverviewQuotes({
    page,
    pageSize: PAGE_SIZE,
    search: debouncedSearch || undefined,
    responseFilter,
  })

  const quotes = data?.data ?? []
  const totalCount = data?.totalCount ?? 0
  const pageCount = data?.pageCount ?? 1

  const filterTabs = useMemo(
    () =>
      RESPONSE_FILTERS.map((filter) => ({
        id: filter.id,
        label: t(filter.labelEn, filter.labelSv),
      })),
    [t]
  )

  return (
    <PageContainer className="flex h-full min-h-0 flex-1 flex-col gap-6">
      <Column className="shrink-0 gap-4">
        <Row className="items-center gap-2">
          <FileText className="h-6 w-6 shrink-0" />
          <Column className="gap-1">
            <h1>{t('Overview', 'Översikt')}</h1>
            <p className="text-muted-foreground">
              {t(
                'All requests in a compact list.',
                'Alla förfrågningar i en kompakt lista.'
              )}
            </p>
          </Column>
        </Row>

        <Row className="items-center gap-3 flex-wrap">
          <Row className="relative min-w-[16rem] flex-1 items-center">
            <Search className="pointer-events-none absolute left-3 h-4 w-4 text-muted-foreground" />
            <Input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder={t(
                'Search name, id, or organization…',
                'Sök namn, id eller organisation…'
              )}
              className="pl-9"
            />
          </Row>

          <TabNavigation
            tabs={filterTabs}
            activeTab={responseFilter}
            onTabChange={(id) => setResponseFilter(id as RequestResponseFilter)}
          />
        </Row>
      </Column>

      <div
        className="flex flex-col overflow-hidden rounded-lg border border-border bg-background"
        style={{ height: 'calc(100dvh - 300px)' }}
      >
        <Table className={overviewTableClass}>
          <OverviewColGroup />
          <TableHeader>
            <TableRow className="border-b bg-muted/40 hover:bg-muted/40">
              <TableHead className={overviewHeadClass}>
                {t('Request', 'Förfrågan')}
              </TableHead>
              <TableHead className={overviewHeadClass}>
                {t('Organization', 'Organisation')}
              </TableHead>
              <TableHead className={`${overviewHeadClass} w-[9.5rem]`}>
                {t('Status', 'Status')}
              </TableHead>
              <TableHead className={`${overviewHeadClass} w-[5.5rem]`}>
                {t('Activity', 'Aktivitet')}
              </TableHead>
              <TableHead className={`${overviewHeadClass} w-[7.5rem] text-right`}>
                {t('Created', 'Skapad')}
              </TableHead>
            </TableRow>
          </TableHeader>
        </Table>

        <div className="min-h-0 flex-1 overflow-y-auto [scrollbar-gutter:stable]">
          <Table className={overviewTableClass}>
            <OverviewColGroup />
            <TableBody>
              {isPending ? (
                <TableRow>
                  <TableCell colSpan={5} className="h-32">
                    <Row className="items-center justify-center gap-2 text-muted-foreground">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      <span>{t('Loading...', 'Laddar...')}</span>
                    </Row>
                  </TableCell>
                </TableRow>
              ) : quotes.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="h-32">
                    <EmptyState
                      icon={
                        <FileText
                          size={36}
                          strokeWidth={2}
                          className="shrink-0"
                        />
                      }
                      title={t(
                        'No requests found',
                        'Inga förfrågningar hittades'
                      )}
                      description={t(
                        'Try another filter or search term.',
                        'Prova ett annat filter eller sökord.'
                      )}
                    />
                  </TableCell>
                </TableRow>
              ) : (
                quotes.map((quote) => (
                  <RequestOverviewRow key={quote.id} quote={quote} />
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      <Row className="shrink-0 items-center justify-between gap-4 flex-wrap">
        <p className="text-sm text-muted-foreground">
          {totalCount === 1
            ? t('1 request', '1 förfrågan')
            : t(`${totalCount} requests`, `${totalCount} förfrågningar`)}
          {isFetching && !isPending ? ` · ${t('Updating…', 'Uppdaterar…')}` : ''}
        </p>

        <Row className="items-center gap-3">
          <span className="text-sm text-muted-foreground">
            {t('Page', 'Sida')} {page + 1} {t('of', 'av')} {pageCount}
          </span>
          <Row className="gap-2">
            <Button
              variant="secondary"
              disabled={page <= 0 || isPending}
              onClick={() => setPage((current) => Math.max(0, current - 1))}
            >
              {t('Previous', 'Föregående')}
            </Button>
            <Button
              variant="secondary"
              disabled={page + 1 >= pageCount || isPending}
              onClick={() =>
                setPage((current) => Math.min(pageCount - 1, current + 1))
              }
            >
              {t('Next', 'Nästa')}
            </Button>
          </Row>
        </Row>
      </Row>
    </PageContainer>
  )
}
